import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sinConexionAhora, useSinConexion } from '@/core/red/conectividad';
import {
  confirmarSincronizada,
  encolarAlta,
  leerCola,
  reintentarRechazada,
  sincronizarCola,
  type AltaEnCola,
  type ResumenAlta,
} from './cola';

interface Cola {
  /** Altas cargadas en el dispositivo que el servidor todavía no confirmó. */
  altas: AltaEnCola[];
  /** Hay una pasada de sincronización en curso. */
  sincronizando: boolean;
  hayConexion: boolean;
  /**
   * Anota un alta para enviar y, si hay señal, la manda enseguida. Devuelve
   * cómo quedó: quien llama NO puede decir "guardado" salvo que esto diga
   * `sincronizado`.
   */
  encolar: (input: {
    organizationId: string;
    payload: Record<string, unknown>;
    resumen: ResumenAlta;
  }) => Promise<
    | { estado: 'sincronizado'; workSessionId: string }
    | { estado: 'pendiente'; motivo?: string }
    | { estado: 'error'; motivo: string }
  >;
  sincronizar: () => Promise<void>;
  reintentar: (clientRequestId: string) => Promise<void>;
}

const Contexto = createContext<Cola | null>(null);

/** Una sola instancia, para no crear un array nuevo en cada render. */
const VACIO: AltaEnCola[] = [];

export function useCola(): Cola {
  const c = useContext(Contexto);
  if (!c) throw new Error('useCola necesita estar dentro de ColaProvider');
  return c;
}

export function ColaProvider({
  organizationId,
  children,
}: {
  organizationId: string | null;
  children: ReactNode;
}) {
  const [sincronizando, setSincronizando] = useState(false);
  const hayConexion = !useSinConexion();
  const queryClient = useQueryClient();
  // Evita dos pasadas simultáneas: la del evento `online` y un toque en
  // "Sincronizar" podrían pisarse. No duplicaría trabajos —para eso está el
  // identificador de operación— pero gasta señal al pedo.
  const enCurso = useRef(false);

  // La cola también es dato que se lee de algún lado, así que va por la misma
  // capa que el resto. Sale más corto que sostenerla con estado y un efecto, y
  // de paso evita el setState sincrónico que eso obliga a escribir.
  //
  // No se persiste: ya vive en IndexedDB. Guardar una copia de la copia sólo
  // agregaría una versión más para desincronizarse.
  const { data: altas = VACIO, refetch } = useQuery({
    queryKey: ['cola', organizationId],
    queryFn: () => leerCola(organizationId!),
    enabled: !!organizationId,
    gcTime: 0,
    staleTime: 0,
    // Sin esto la cola no se puede leer justo cuando hace falta. TanStack Query
    // PAUSA las consultas mientras el navegador se declara sin señal, porque da
    // por sentado que toda consulta va a la red. Ésta no: lee el teléfono. Sin
    // señal la promesa quedaba colgada para siempre, y el formulario con ella,
    // mostrando "Guardando…" sobre un trabajo que ya estaba guardado.
    networkMode: 'always',
  });

  const releer = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const sincronizar = useCallback(async () => {
    if (!organizationId || enCurso.current) return;
    enCurso.current = true;
    setSincronizando(true);
    try {
      await sincronizarCola(organizationId);

      // Recién ahora se sacan de la cola las confirmadas, y sólo después de
      // refrescar la lista: así el trabajo real aparece en pantalla antes de
      // que desaparezca su borrador, y nunca queda un hueco donde el trabajo no
      // se vea en ningún lado.
      const despues = await leerCola(organizationId);
      const confirmadas = despues.filter((a) => a.estado === 'sincronizado');
      if (confirmadas.length > 0) {
        await queryClient.invalidateQueries({ queryKey: ['trabajos'] });
        for (const a of confirmadas) await confirmarSincronizada(a.clientRequestId);
      }
      await releer();
    } finally {
      enCurso.current = false;
      setSincronizando(false);
    }
  }, [organizationId, queryClient, releer]);

  // Al volver la señal se intenta solo. Es el caso central: la persona cargó
  // trabajos en el campo, subió a la camioneta y volvió al pueblo.
  //
  // Con una espera corta a propósito. El evento `online` llega apenas hay una
  // interfaz de red levantada, que suele ser antes de que los paquetes lleguen
  // a ningún lado; salir disparando produce un fallo que después hay que
  // reintentar igual.
  useEffect(() => {
    if (!hayConexion) return;
    const t = setTimeout(() => void sincronizar(), 1500);
    return () => clearTimeout(t);
  }, [hayConexion, sincronizar]);

  const encolar = useCallback<Cola['encolar']>(
    async (input) => {
      const alta = await encolarAlta(input);
      await releer();

      // Se decide con la conectividad COMPROBADA, no con `navigator.onLine`:
      // en el teléfono, en modo avión, ése devolvió `true` y la aplicación
      // salía a intentar el envío igual. El alta ya está guardada acá, así que
      // el intento fallido no perdía nada, pero mostraba un error donde
      // correspondía decir «guardado en este teléfono».
      if (sinConexionAhora()) return { estado: 'pendiente' };

      const { intentarEnviar } = await import('./cola');
      const r = await intentarEnviar(alta.clientRequestId);
      if (r.estado === 'sincronizado') {
        await queryClient.invalidateQueries({ queryKey: ['trabajos'] });
        await confirmarSincronizada(alta.clientRequestId);
      }
      await releer();
      return r;
    },
    [queryClient, releer],
  );

  const reintentar = useCallback(
    async (clientRequestId: string) => {
      await reintentarRechazada(clientRequestId);
      await releer();
      await sincronizar();
    },
    [releer, sincronizar],
  );

  // Quien trabaja en dos veterinarias no puede ver los borradores de una
  // mientras opera la otra, ni siquiera por un render de más mientras se relee.
  const propias = organizationId ? altas.filter((a) => a.organizationId === organizationId) : VACIO;

  return (
    <Contexto.Provider
      value={{ altas: propias, sincronizando, hayConexion, encolar, sincronizar, reintentar }}
    >
      {children}
    </Contexto.Provider>
  );
}
